import clsx from "clsx";
import { generateFileURL } from "~/utils/file/generateFileURL";
import type { CompanyGalleryDB } from "~/database/company_galleries.model";
import { useState, useRef } from "react";
import {
  useFetcher,
  Link,
} from "@remix-run/react";
import { useUpload } from "~/utils/file/uploadFileToR2";
import toast from "react-hot-toast";

interface CompanyGalleriesListProps {
  galleries: CompanyGalleryDB[];
  basePath?: string;
  isEditable?: boolean;
  userId?: string;
  onEdit?: (
    galleries: CompanyGalleryDB[],
  ) => void;
}

export default function CompanyGalleriesList({
  galleries,
  basePath,
  isEditable = false,
  userId,
  onEdit,
}: CompanyGalleriesListProps) {
  const [isEditMode, setIsEditMode] =
    useState(false);
  const [editingGalleries, setEditingGalleries] =
    useState<CompanyGalleryDB[]>(galleries);
  const fetcher = useFetcher();
  const uploadingIndex = useRef<number | null>(
    null,
  );

  // 画像アップロード用のuseUploadフック
  const { imageSelect } = useUpload(
    (path: string) => {
      if (uploadingIndex.current !== null) {
        const index = uploadingIndex.current;
        const updated = [...editingGalleries];
        updated[index] = {
          ...updated[index],
          image_url: path,
        };
        setEditingGalleries(updated);
        uploadingIndex.current = null;
      }
    },
  );

  const handleAddGallery = () => {
    const newGallery: CompanyGalleryDB = {
      id: `temp-${Date.now()}`,
      user_id:
        userId || galleries[0]?.user_id || "",
      image_url: "",
      caption: "",
      display_order: editingGalleries.length,
      is_public: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setEditingGalleries([
      ...editingGalleries,
      newGallery,
    ]);
  };

  const handleUpdateGallery = (
    index: number,
    field: keyof CompanyGalleryDB,
    value: any,
  ) => {
    const updated = [...editingGalleries];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setEditingGalleries(updated);
  };

  const handleDeleteGallery = (index: number) => {
    setEditingGalleries(
      editingGalleries.filter(
        (_, i) => i !== index,
      ),
    );
  };

  const handleImageUpload = async (
    index: number,
    file: File,
  ) => {
    // ファイルタイプチェック
    const acceptType = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "image/gif",
      "image/webp",
    ];
    if (!acceptType.includes(file.type)) {
      toast.error(
        "画像ファイルを選択してください。",
        { duration: 4000 },
      );
      return;
    }

    // ファイルサイズチェック（5MB制限）
    const maxSize = 1024 * 1024 * 5;
    if (file.size > maxSize) {
      toast.error(
        "5MB以下の画像ファイルを選択してください。",
        { duration: 4000 },
      );
      return;
    }

    uploadingIndex.current = index;
    imageSelect(file);
  };

  const handleSave = () => {
    const formData = new FormData();
    formData.append(
      "galleries",
      JSON.stringify(editingGalleries),
    );

    fetcher.submit(formData, {
      method: "post",
      action: "/api/company-galleries/update",
    });

    onEdit?.(editingGalleries);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setEditingGalleries(galleries);
    setIsEditMode(false);
  };

  return (
    <div className={clsx("mt-60")}>
      <div
        className={clsx(
          "mb-24 flex items-center justify-between",
        )}
      >
        <h3
          className={clsx(
            "text-40 font-bold",
            "max-md:text-24",
          )}
        >
          GALLERY
        </h3>
        {isEditable && !isEditMode && (
          <button
            onClick={() => setIsEditMode(true)}
            className={clsx(
              "text-12",
              "px-16",
              "py-6",
              "border",
              "border-gray-300",
              "rounded-full",
              "hover:bg-gray-50",
              "transition-colors",
            )}
          >
            {galleries.length === 0
              ? "追加"
              : "編集"}
          </button>
        )}

        {isEditMode && (
          <div
            className={clsx(
              "flex",
              "gap-12",
              "justify-end",
            )}
          >
            <button
              onClick={handleCancel}
              className={clsx(
                "px-20",
                "py-8",
                "border",
                "rounded-full",
                "text-12",
              )}
            >
              キャンセル
            </button>
            <button
              onClick={handleSave}
              className={clsx(
                "px-20",
                "py-8",
                "bg-black",
                "text-white",
                "rounded-full",
                "text-12",
              )}
            >
              保存
            </button>
          </div>
        )}
      </div>

      {galleries.length === 0 && !isEditMode && (
        <div
          className={clsx(
            "text-center",
            "py-40",
            "text-gray-400",
            "text-14",
          )}
        >
          ギャラリー画像が登録されていません
        </div>
      )}

      <div
        className={clsx(
          "grid grid-cols-3 gap-24",
          "max-md:grid-cols-2",
          "max-md:gap-16",
        )}
      >
        {(isEditMode
          ? editingGalleries
          : galleries.slice(0, 6)
        ).map((gallery, index) => (
          <div
            key={gallery.id}
            className={clsx(
              "relative",
              "group",
              isEditMode &&
                "rounded-lg bg-gray-50 p-8",
            )}
          >
            {isEditMode && (
              <button
                onClick={() =>
                  handleDeleteGallery(index)
                }
                className={clsx(
                  "absolute",
                  "top-12",
                  "right-12",
                  "z-10",
                  "w-24",
                  "h-24",
                  "flex",
                  "items-center",
                  "justify-center",
                  "bg-red-500",
                  "text-white",
                  "rounded-full",
                  "text-14",
                )}
              >
                ×
              </button>
            )}

            <div
              className={clsx(
                "aspect-video",
                "bg-gray-100",
                "rounded-12",
                "overflow-hidden",
                "relative",
              )}
            >
              {gallery.image_url ? (
                <img
                  src={generateFileURL(
                    gallery.image_url,
                    undefined,
                    basePath,
                  )}
                  alt={gallery.caption || ""}
                  className={clsx(
                    "w-full",
                    "h-full",
                    "object-cover",
                  )}
                />
              ) : (
                <div
                  className={clsx(
                    "w-full",
                    "h-full",
                    "flex",
                    "items-center",
                    "justify-center",
                    "text-gray-400",
                  )}
                >
                  画像なし
                </div>
              )}

              {isEditMode && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file =
                        e.target.files?.[0];
                      if (file) {
                        handleImageUpload(
                          index,
                          file,
                        );
                      }
                    }}
                    className="hidden"
                    id={`gallery-image-${gallery.id}`}
                  />
                  <label
                    htmlFor={`gallery-image-${gallery.id}`}
                    className={clsx(
                      "absolute",
                      "inset-0",
                      "bg-black",
                      "bg-opacity-50",
                      "text-white",
                      "text-12",
                      "flex",
                      "items-center",
                      "justify-center",
                      "cursor-pointer",
                    )}
                  >
                    画像を変更
                  </label>
                </>
              )}
            </div>

            {isEditMode ? (
              <textarea
                value={gallery.caption || ""}
                onChange={(e) =>
                  handleUpdateGallery(
                    index,
                    "caption",
                    e.target.value,
                  )
                }
                placeholder="キャプションを入力"
                className={clsx(
                  "w-full",
                  "mt-8",
                  "p-8",
                  "border",
                  "rounded",
                  "text-12",
                  "resize-none",
                )}
                rows={2}
              />
            ) : (
              gallery.caption && (
                <div
                  className={clsx(
                    "mt-8",
                    "text-12",
                    "text-gray-600",
                  )}
                >
                  {gallery.caption}
                </div>
              )
            )}
          </div>
        ))}

        {isEditMode && (
          <button
            onClick={handleAddGallery}
            className={clsx(
              "aspect-video",
              "border",
              "border-dashed",
              "border-gray-300",
              "rounded-lg",
              "flex",
              "items-center",
              "justify-center",
              "hover:bg-gray-50",
            )}
          >
            <span
              className={clsx(
                "text-32",
                "text-gray-400",
              )}
            >
              +
            </span>
          </button>
        )}
      </div>

      {!isEditMode && galleries.length > 0 && (
        <div
          className={clsx("text-center", "mt-32")}
        >
          <Link
            to={`/user/${userId}/galleries`}
            className={clsx(
              "inline-block",
              "px-32",
              "py-12",
              "border",
              "border-gray-300",
              "rounded-full",
              "text-14",
              "hover:bg-gray-50",
              "transition-colors",
            )}
          >
            全てのギャラリーを見る（
            {galleries.length}枚）
          </Link>
        </div>
      )}
    </div>
  );
}
